'use client';

import { useState } from 'react';

interface BookerSProps {
    calendarUrl: string;
    title: string;
}

/**
 * Cal.com booking embed rendered behind a click-to-load facade.
 *
 * Embedding the cal.com <iframe> eagerly dropped third-party cookies
 * (__cf_bm, next-auth.csrf-token, …) on page load, failing Lighthouse's
 * `third-party-cookies` / `inspector-issues` (best-practices) audits, and the
 * late-loading iframe shifted surrounding content (CLS). The facade reserves the
 * full embed height up-front (no layout shift) and only loads cal.com — and its
 * cookies — after the user opts in, which is also the privacy-friendly default.
 */
export default function BookerS(props: BookerSProps) {
    const [loaded, setLoaded] = useState(false);

    return (
        <div className="relative w-full h-[700px] overflow-hidden rounded-lg">
            {loaded ? (
                <iframe
                    src={props.calendarUrl}
                    loading="lazy"
                    title={props.title}
                    className="w-full h-full border-none"
                />
            ) : (
                <button
                    type="button"
                    onClick={() => setLoaded(true)}
                    className="flex h-full w-full flex-col items-center justify-center gap-3 bg-[var(--primary--beige)] text-[var(--primary--burgundy)] transition-colors hover:bg-[var(--pink--50)] dark:bg-[var(--gray--100)] dark:text-[var(--primary--pink)]"
                    aria-label={`Load booking calendar to ${props.title}`}
                >
                    <svg
                        className="h-10 w-10"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.5}
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0V11.25A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
                        />
                    </svg>
                    <span className="text-size-medium font-medium">Load booking calendar</span>
                    <span className="text-size-small opacity-80">
                        Opens cal.com to pick a time. Loading it sets third-party cookies.
                    </span>
                </button>
            )}
        </div>
    );
}
